import { useState, useRef } from 'react';
import { Upload, Sparkles, CheckCircle, XCircle, AlertTriangle, Download, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminAxios } from './AdminLayout';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBulkImport() {
  var [step, setStep] = useState('input'); // input, review, result
  var [rows, setRows] = useState([]);
  var [rawText, setRawText] = useState('');
  var [parsing, setParsing] = useState(false);
  var [importing, setImporting] = useState(false);
  var [compliance, setCompliance] = useState(false);
  var [result, setResult] = useState(null);
  var fileRef = useRef(null);

  async function handleParseText() {
    if (!rawText.trim()) return;
    setParsing(true);
    try {
      var res = await adminAxios().post('/api/admin/clients/parse-text', { raw_text: rawText });
      setRows(res.data.rows.map(function(r) { return Object.assign({}, r, { action: r.is_duplicate ? 'skip' : 'import', subscribed: true }); }));
      setStep('review');
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'Parsing failed'); }
    finally { setParsing(false); }
  }

  async function handleFileUpload(e) {
    var file = e.target.files[0];
    if (!file) return;
    setParsing(true);
    try {
      var fd = new FormData();
      fd.append('file', file);
      var res = await adminAxios().post('/api/admin/clients/parse-csv', fd);
      setRows(res.data.rows.map(function(r) { return Object.assign({}, r, { action: r.is_duplicate ? 'skip' : 'import', subscribed: true }); }));
      setStep('review');
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'File parsing failed'); }
    finally { setParsing(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  function updateRow(idx, key, val) {
    var nr = rows.slice();
    nr[idx] = Object.assign({}, nr[idx]);
    nr[idx][key] = val;
    setRows(nr);
  }

  function removeRow(idx) {
    setRows(rows.filter(function(_, i) { return i !== idx; }));
  }

  async function handleImport() {
    if (!compliance) { alert('Please confirm the compliance checkbox'); return; }
    setImporting(true);
    try {
      var res = await adminAxios().post('/api/admin/clients/bulk-import', { rows: rows, compliance_confirmed: true });
      setResult(res.data);
      setStep('result');
    } catch (err) { alert(typeof (err.response&&err.response.data&&err.response.data.detail)==='string'?err.response.data.detail:'Import failed'); }
    finally { setImporting(false); }
  }

  var importCount = rows.filter(function(r) { return r.action === 'import'; }).length;
  var updateCount = rows.filter(function(r) { return r.action === 'update'; }).length;
  var skipCount = rows.filter(function(r) { return r.action === 'skip'; }).length;
  var dupeCount = rows.filter(function(r) { return r.is_duplicate; }).length;

  return (
    <div data-testid="admin-bulk-import-page">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/admin/clients" className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft className="w-5 h-5 text-slate-500" /></Link>
        <h2 className="font-outfit text-2xl font-bold text-slate-900">AI Bulk Import</h2>
      </div>

      {step === 'input' && (
        <div className="space-y-6">
          {/* Paste Text */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-[#0096D6]" />
              <h3 className="font-outfit text-lg font-semibold text-slate-900">Paste Contact List</h3>
            </div>
            <p className="font-manrope text-sm text-slate-500 mb-3">Paste raw text — AI will extract names, companies, emails, and other details automatically.</p>
            <textarea value={rawText} onChange={function(e) { setRawText(e.target.value); }} className="w-full px-4 py-3 rounded-xl border border-slate-200 font-manrope text-sm h-40 resize-none" placeholder={"John Smith - john@company.com - ABC Traders - UK\nMary Jones <mary@example.com>, VAT GB123456789\nsales@company.co.uk - Company XYZ - Germany"} data-testid="bulk-import-textarea" />
            <button onClick={handleParseText} disabled={parsing || !rawText.trim()} className="mt-3 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50" data-testid="parse-text-button">
              <Sparkles className="w-4 h-4" /> {parsing ? 'AI Parsing...' : 'Parse with AI'}
            </button>
          </div>

          {/* Upload File */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Upload className="w-5 h-5 text-[#0096D6]" />
              <h3 className="font-outfit text-lg font-semibold text-slate-900">Upload File</h3>
            </div>
            <p className="font-manrope text-sm text-slate-500 mb-3">Upload CSV, Excel (XLSX), or TXT file. Columns are mapped automatically.</p>
            <label className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-[#0096D6] text-[#0096D6] font-manrope text-sm font-semibold cursor-pointer hover:bg-[#0096D6]/5">
              <Upload className="w-4 h-4" /> {parsing ? 'Processing...' : 'Choose File'}
              <input ref={fileRef} type="file" className="hidden" accept=".csv,.xlsx,.xls,.txt" onChange={handleFileUpload} />
            </label>
          </div>

          {/* Export */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Download className="w-5 h-5 text-[#0096D6]" />
              <h3 className="font-outfit text-lg font-semibold text-slate-900">Export Clients</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {[{g:'all',l:'All Clients'},{g:'subscribed',l:'Subscribed'},{g:'newsletter',l:'Newsletter'},{g:'website_enquiry',l:'Website Enquiries'},{g:'bulk_import',l:'Bulk Imports'},{g:'manual',l:'Manual'}].map(function(item) {
                return <a key={item.g} href={BACKEND_URL + '/api/admin/clients/export?group=' + item.g} target="_blank" rel="noreferrer" className="px-4 py-2 rounded-lg border border-slate-200 font-manrope text-xs font-medium text-slate-600 hover:border-[#0096D6] hover:text-[#0096D6]" onClick={function(e) {
                  e.preventDefault();
                  var token = localStorage.getItem('admin_token');
                  fetch(BACKEND_URL + '/api/admin/clients/export?group=' + item.g, { headers: { Authorization: 'Bearer ' + token } })
                    .then(function(r) { return r.blob(); })
                    .then(function(blob) { var url = URL.createObjectURL(blob); var a = document.createElement('a'); a.href = url; a.download = 'clients_' + item.g + '.csv'; a.click(); });
                }}>{item.l}</a>;
              })}
            </div>
          </div>
        </div>
      )}

      {step === 'review' && (
        <div>
          {/* Summary bar */}
          <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm mb-4 flex flex-wrap gap-4 items-center">
            <span className="font-manrope text-sm"><strong>{rows.length}</strong> contacts parsed</span>
            <span className="font-manrope text-sm text-green-600"><strong>{importCount}</strong> to import</span>
            <span className="font-manrope text-sm text-blue-600"><strong>{updateCount}</strong> to update</span>
            <span className="font-manrope text-sm text-slate-400"><strong>{skipCount}</strong> to skip</span>
            {dupeCount > 0 && <span className="font-manrope text-sm text-amber-600"><AlertTriangle className="w-3 h-3 inline" /> <strong>{dupeCount}</strong> duplicates</span>}
          </div>

          {/* Review table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Action</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Name</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Company</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Email</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Country</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">VAT</th>
                    <th className="text-left px-3 py-2 font-manrope font-semibold text-slate-600">Status</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(function(row, idx) {
                    return (
                      <tr key={idx} className={'border-b border-slate-50 ' + (row.is_duplicate ? 'bg-amber-50/50' : '')}>
                        <td className="px-3 py-2">
                          <select value={row.action} onChange={function(e) { updateRow(idx, 'action', e.target.value); }} className="px-2 py-1 rounded border border-slate-200 font-manrope text-xs">
                            <option value="import">Import</option>
                            {row.is_duplicate && <option value="update">Update</option>}
                            <option value="skip">Skip</option>
                          </select>
                        </td>
                        <td className="px-3 py-2"><input value={row.full_name || ''} onChange={function(e) { updateRow(idx, 'full_name', e.target.value); }} className="px-2 py-1 rounded border border-slate-200 font-manrope text-xs w-28" /></td>
                        <td className="px-3 py-2"><input value={row.company_name || ''} onChange={function(e) { updateRow(idx, 'company_name', e.target.value); }} className="px-2 py-1 rounded border border-slate-200 font-manrope text-xs w-28" /></td>
                        <td className="px-3 py-2 font-manrope text-xs text-slate-700">{row.email}{row.is_duplicate && <span className="ml-1 text-amber-600 text-[10px]">(exists)</span>}</td>
                        <td className="px-3 py-2"><input value={row.country || ''} onChange={function(e) { updateRow(idx, 'country', e.target.value); }} className="px-2 py-1 rounded border border-slate-200 font-manrope text-xs w-20" /></td>
                        <td className="px-3 py-2"><input value={row.vat_number || ''} onChange={function(e) { updateRow(idx, 'vat_number', e.target.value); }} className="px-2 py-1 rounded border border-slate-200 font-manrope text-xs w-24" /></td>
                        <td className="px-3 py-2">
                          <button onClick={function() { updateRow(idx, 'subscribed', !row.subscribed); }} className={'px-2 py-0.5 rounded-full text-[10px] font-semibold ' + (row.subscribed ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500')}>
                            {row.subscribed ? 'Subscribed' : 'Unsubscribed'}
                          </button>
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={function() { removeRow(idx); }} className="text-red-400 hover:text-red-600 text-xs">Remove</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Compliance + Import */}
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <div className="flex items-start gap-3 mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <input type="checkbox" checked={compliance} onChange={function(e) { setCompliance(e.target.checked); }} className="w-4 h-4 mt-0.5 rounded" id="compliance-check" data-testid="compliance-checkbox" />
              <label htmlFor="compliance-check" className="font-manrope text-sm text-slate-700">
                I confirm these contacts are business contacts and may be added to the Real Tech Services distribution list in accordance with applicable consent or legitimate interest rules.
              </label>
            </div>
            <div className="flex items-center justify-between">
              <button onClick={function() { setStep('input'); setRows([]); setCompliance(false); }} className="px-4 py-2 rounded-xl border border-slate-200 font-manrope text-sm">Back</button>
              <button onClick={handleImport} disabled={importing || !compliance || importCount + updateCount === 0} className="px-6 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold disabled:opacity-50" data-testid="import-button">
                {importing ? 'Importing...' : 'Import ' + (importCount + updateCount) + ' Contacts'}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'result' && result && (
        <div className="bg-white rounded-2xl border border-slate-100 p-8 shadow-sm text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="font-outfit text-2xl font-bold text-slate-900 mb-4">Import Complete</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-lg mx-auto mb-6">
            <div className="bg-green-50 rounded-xl p-4"><p className="font-space text-2xl font-bold text-green-600">{result.imported}</p><p className="font-manrope text-xs text-green-700">Imported</p></div>
            <div className="bg-blue-50 rounded-xl p-4"><p className="font-space text-2xl font-bold text-blue-600">{result.updated}</p><p className="font-manrope text-xs text-blue-700">Updated</p></div>
            <div className="bg-slate-50 rounded-xl p-4"><p className="font-space text-2xl font-bold text-slate-500">{result.skipped}</p><p className="font-manrope text-xs text-slate-600">Skipped</p></div>
            <div className="bg-red-50 rounded-xl p-4"><p className="font-space text-2xl font-bold text-red-500">{result.invalid}</p><p className="font-manrope text-xs text-red-600">Invalid</p></div>
          </div>

          {/* Classification Stats */}
          {result.classification && (
            <div className="max-w-md mx-auto mb-6" data-testid="import-classification-stats">
              <p className="font-manrope text-xs font-semibold text-slate-600 mb-3 uppercase tracking-wider">Market Classification</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100"><p className="font-space text-lg font-bold text-blue-700">{result.classification.uk || 0}</p><p className="font-manrope text-[10px] text-blue-600">UK</p></div>
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100"><p className="font-space text-lg font-bold text-emerald-700">{result.classification.international || 0}</p><p className="font-manrope text-[10px] text-emerald-600">International</p></div>
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100"><p className="font-space text-lg font-bold text-amber-700">{result.classification.special_review || 0}</p><p className="font-manrope text-[10px] text-amber-600">Special Review</p></div>
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-200"><p className="font-space text-lg font-bold text-slate-500">{result.classification.unclassified || 0}</p><p className="font-manrope text-[10px] text-slate-500">Unclassified</p></div>
              </div>
            </div>
          )}

          <div className="flex justify-center gap-3">
            <Link to="/admin/clients" className="px-5 py-2.5 rounded-xl bg-[#0096D6] text-white font-manrope text-sm font-semibold">View Clients</Link>
            <button onClick={function() { setStep('input'); setRows([]); setResult(null); setCompliance(false); setRawText(''); }} className="px-5 py-2.5 rounded-xl border border-slate-200 font-manrope text-sm">Import More</button>
          </div>
        </div>
      )}
    </div>
  );
}
